import { Pipe, PipeTransform, Injectable, ɵstringify } from '@angular/core';
import { DateHelper } from '../helpers/date.helper';

// eslint-disable-next-line @typescript-eslint/naming-convention
declare const Intl: any;

@Pipe({
  name: 'relativeTime',
  standalone: false,
})
@Injectable()
export class RelativeTimePipe implements PipeTransform {
  constructor(private dateHelper: DateHelper) {}

  transform(
    value: string,
    language?: string,
    capOnDaysWhenFuture = false, // Whether days is the max formatter when the date is in the future
    isOrganisationCallDeadline = false, // Whether the date is the deadline of an organisation call
  ): string {
    if (!value) {
      return value;
    }

    // The date to compare to. Assumes that it's in the Europe/Brussels timezone
    const date = new Date(
      `${value}${isOrganisationCallDeadline ? 'T23:59:59' : ''}`,
    );

    // Convert the user's current date to the Europe/Brussels timezone
    const now = this.dateHelper.getNowDateForUser();

    /* If an invalid string was passed, the epoch will be `NaN` */
    if (isNaN(date.getTime())) {
      /* Using the same error message style as Angular */
      throw Error(
        `InvalidPipeArgument: '${value}' for pipe '${ɵstringify(RelativeTimePipe)}'`,
      );
    }

    const locale = language || navigator.language;
    const diffInMilliSecs = date.getTime() - now.getTime();
    const diffInSecs = Math.trunc(diffInMilliSecs / 1000);

    /* Take the absolute difference.
    Doesn't matter whether `date` is in the past or future. We only want the difference. */
    const absDiffInSecs = Math.abs(diffInSecs);

    /* Parentheses are needed around `case` blocks to redeclare the `formatter` variable */
    switch (true) {
      case absDiffInSecs < 60: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        return formatter.format(diffInSecs, 'second');
      }

      case absDiffInSecs >= 60 && absDiffInSecs < 3600: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const minutes = Math.trunc(diffInSecs / 60);

        return formatter.format(minutes, 'minute');
      }

      case absDiffInSecs > 3600 && absDiffInSecs < 86400: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const hours = Math.trunc(diffInSecs / 3600);

        return formatter.format(hours, 'hour');
      }

      case (absDiffInSecs >= 86400 && absDiffInSecs < 604800) ||
        (diffInSecs > 0 && capOnDaysWhenFuture): {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const days = Math.trunc(diffInSecs / 86400);

        return formatter.format(days, 'day');
      }

      case absDiffInSecs >= 604800 && absDiffInSecs < 2592000: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const weeks = Math.trunc(diffInSecs / 604800);

        return formatter.format(weeks, 'week');
      }

      case absDiffInSecs >= 2592000 && absDiffInSecs < 31536000: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const months = Math.trunc(diffInSecs / 2592000);

        return formatter.format(months, 'month');
      }

      case absDiffInSecs >= 31536000: {
        const formatter = new Intl.RelativeTimeFormat(locale, {
          numeric: 'auto',
        });
        const years = Math.trunc(diffInSecs / 31536000);

        return formatter.format(years, 'year');
      }

      default:
        return '';
    }
  }
}
